/**
 * WebMCP tool schemas for Bandmate. **Frozen contract** — the single source of
 * truth for every tool's name, description, JSON Schema and annotations
 * (SPEC §5). `tools/index.ts` attaches implementations; `scripts/docs.ts`
 * renders `docs/tools.md`; `schemas.test.ts` enforces the hygiene rules.
 *
 * Hygiene (SPEC §5, panel webmcp-12 / chrome-i11 / eng-9 / webmcp-15):
 * - every schema is `type:'object'`, `additionalProperties:false`, with an
 *   explicit `required` array (possibly empty);
 * - every closed set is an `enum`; every property has a description ≤ 120
 *   chars that mentions every enum value; numbers carry `minimum`/`maximum`,
 *   arrays `minItems`/`maxItems`;
 * - names ≤ 30 chars, descriptions ≤ 350 chars, verb-first, exactly one
 *   "Use when …" sentence and one constraint; writers end with
 *   {@link WRITER_SENTENCE};
 * - readers carry `readOnlyHint`; `clear_track` alone carries
 *   `consequentialHint`; nothing carries `untrustedContentHint`.
 *
 * The descriptions are the only routing signal the agent gets, so the pairs
 * that are easy to confuse point at each other: `add_chords` (a progression)
 * vs `write_notes` (individual notes) and `add_drums` (a whole beat) vs
 * `set_drum_steps` (one lane).
 */
import {
  BASS_PATTERNS,
  CHORD_RHYTHMS,
  DENSITIES,
  DRUM_LANES,
  KEY_SPELLINGS,
  MELODIC_TRACK_IDS,
  MODES,
  STYLES,
  SWING_MAX,
  SWING_MIN,
  TEMPO_MAX,
  TEMPO_MIN,
  TRACK_IDS,
  VOLUME_MAX_DB,
  VOLUME_MIN_DB,
  type BassPattern,
  type ChordRhythm,
  type Density,
  type DrumLane,
  type MelodicTrackId,
  type Mode,
  type StyleId,
  type TrackId,
} from '../types';

// ---------------------------------------------------------------------------
// Names, groups, annotations
// ---------------------------------------------------------------------------

/** Lifecycle groups (SPEC §5). Readers, dialog and transport are always registered. */
export type ToolGroup = 'readers' | 'writers' | 'dialog' | 'transport' | 'recording';

/** Every tool name, in display order. */
export const TOOL_NAMES = [
  'get_session',
  'get_track',
  'suggest_chords',
  'set_groove',
  'add_chords',
  'add_bass',
  'add_drums',
  'write_notes',
  'set_drum_steps',
  'set_track',
  'clear_track',
  'audition',
  'undo',
  'transport',
  'export_midi',
  'stop_recording',
] as const;

/** Union of all tool names. */
export type ToolName = (typeof TOOL_NAMES)[number];

/** Which tools belong to which lifecycle group. Sizes: readers 3, writers 8, dialog 2, transport 2, recording 1. */
export const TOOL_GROUPS: Readonly<Record<ToolGroup, readonly ToolName[]>> = {
  readers: ['get_session', 'get_track', 'suggest_chords'],
  writers: ['set_groove', 'add_chords', 'add_bass', 'add_drums', 'write_notes', 'set_drum_steps', 'set_track', 'clear_track'],
  dialog: ['audition', 'undo'],
  transport: ['transport', 'export_midi'],
  recording: ['stop_recording'],
};

/** The only two annotations Bandmate uses (SPEC §5: no `untrustedContentHint`). */
export interface ToolAnnotations {
  /** The tool changes nothing. Set on every reader. */
  readOnlyHint?: boolean;
  /** The tool destroys human work; the agent should confirm intent. Set on `clear_track` only. */
  consequentialHint?: boolean;
}

/** Sentence every writer description ends with (SPEC §5). */
export const WRITER_SENTENCE = 'Visible in the grid immediately; the loop keeps playing; returns undo_id.';

// ---------------------------------------------------------------------------
// JSON Schema shapes
// ---------------------------------------------------------------------------

/** The JSON Schema subset Bandmate emits (draft 2020-12 compatible, Ajv strict-clean). */
export interface PropertySchema {
  type: 'string' | 'number' | 'integer' | 'boolean' | 'array' | 'object';
  description: string;
  enum?: readonly (string | number)[];
  default?: string | number | boolean;
  minimum?: number;
  maximum?: number;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  minItems?: number;
  maxItems?: number;
  uniqueItems?: boolean;
  items?: PropertySchema;
  properties?: Record<string, PropertySchema>;
  required?: readonly string[];
  additionalProperties?: false;
}

/** Top-level input schema: always a closed object with an explicit `required` list. */
export interface ObjectSchema {
  type: 'object';
  properties: Record<string, PropertySchema>;
  required: readonly string[];
  additionalProperties: false;
}

/** One tool's static definition: everything WebMCP needs except `execute`. */
export interface ToolSchema {
  name: ToolName;
  description: string;
  inputSchema: ObjectSchema;
  annotations?: ToolAnnotations;
  group: ToolGroup;
}

// ---------------------------------------------------------------------------
// Shared fragments
// ---------------------------------------------------------------------------

const TRACK_LIST = TRACK_IDS.join(', ');
const MELODIC_LIST = MELODIC_TRACK_IDS.join(', ');

const trackProp = (description: string): PropertySchema => ({
  type: 'string',
  enum: TRACK_IDS,
  description,
});

const barsProp = (description: string): PropertySchema => ({
  type: 'array',
  description,
  minItems: 1,
  maxItems: 4,
  uniqueItems: true,
  items: { type: 'integer', minimum: 1, maximum: 4, description: 'Bar number, 1 to 4.' },
});

const progressionProp = (description: string): PropertySchema => ({
  type: 'array',
  description,
  minItems: 1,
  maxItems: 4,
  items: {
    type: 'string',
    minLength: 1,
    maxLength: 12,
    description: 'One chord as a Roman numeral (i, VI, bVII, V7, iiø7) or a chord name (Am, Fmaj7, G7, Bb).',
  },
});

const rhythmProp = (description: string): PropertySchema => ({
  type: 'string',
  enum: CHORD_RHYTHMS,
  description,
});

const bassPatternProp = (description: string): PropertySchema => ({
  type: 'string',
  enum: BASS_PATTERNS,
  description,
});

const densityProp = (description: string): PropertySchema => ({
  type: 'string',
  enum: DENSITIES,
  description,
});

const tempoProp = (description: string): PropertySchema => ({
  type: 'integer',
  minimum: TEMPO_MIN,
  maximum: TEMPO_MAX,
  description,
});

const swingProp = (description: string): PropertySchema => ({
  type: 'integer',
  minimum: SWING_MIN,
  maximum: SWING_MAX,
  description,
});

const EMPTY: ObjectSchema = { type: 'object', properties: {}, required: [], additionalProperties: false };

// ---------------------------------------------------------------------------
// The tools
// ---------------------------------------------------------------------------

/** All 16 tools keyed by name. Idle registers 15 (everything but `stop_recording`). */
export const TOOLS: Readonly<Record<ToolName, ToolSchema>> = {
  // ---- readers ----
  get_session: {
    name: 'get_session',
    group: 'readers',
    annotations: { readOnlyHint: true },
    description:
      'Read the whole session in one call: key, tempo, swing, style, mode, position, per-track note counts split ' +
      'human/agent, the progression, recent human actions, undo depth and any open audition. ' +
      'Use when you need context before acting or after the human changed something. ' +
      'Returns a summary under 700 characters, not the notes; call get_track for those.',
    inputSchema: EMPTY,
  },

  get_track: {
    name: 'get_track',
    group: 'readers',
    annotations: { readOnlyHint: true },
    description:
      'Read one track as compact per-bar text: scale degree@step for bass, chords and lead, or 16-character lane ' +
      'patterns for drums, with human and agent note counts. ' +
      'Use when the human asks what they just played or you need exact pitches before harmonising or editing. ' +
      'Pass bars to read a subset; the reply stays under 1500 characters.',
    inputSchema: {
      type: 'object',
      properties: {
        track: trackProp(`Track to read: ${TRACK_LIST}.`),
        bars: barsProp('Bars to include, 1 to 4 (1 to 4 entries). Omit for all four bars.'),
      },
      required: ['track'],
      additionalProperties: false,
    },
  },

  suggest_chords: {
    name: 'suggest_chords',
    group: 'readers',
    annotations: { readOnlyHint: true },
    description:
      'Analyse the human melody on the lead track and rank 4-bar chord progressions that fit it, with detected key ' +
      'candidates and a reason per suggestion; writes nothing. ' +
      'Use when the human asks what chords or harmony go under what they played, before calling add_chords. ' +
      'Needs notes on the lead track; returns error no_melody otherwise.',
    inputSchema: {
      type: 'object',
      properties: {
        n: {
          type: 'integer',
          minimum: 1,
          maximum: 5,
          default: 3,
          description: 'How many suggestions to return, 1 to 5. Default 3.',
        },
      },
      required: [],
      additionalProperties: false,
    },
  },

  // ---- writers ----
  set_groove: {
    name: 'set_groove',
    group: 'writers',
    description:
      'Set tempo, key, mode, swing, style or metronome in one call; notes transpose to a new key unless ' +
      'transpose_content is false. ' +
      'Use when the human wants it faster, slower, in another key, more swing, or another sound (lofi, house, synthwave). ' +
      'Only the fields you pass change. ' +
      WRITER_SENTENCE,
    inputSchema: {
      type: 'object',
      properties: {
        tempo: tempoProp(`Tempo in BPM, ${TEMPO_MIN} to ${TEMPO_MAX}.`),
        key: {
          type: 'string',
          enum: KEY_SPELLINGS,
          description: `Tonic, sharps or flats: ${KEY_SPELLINGS.join(', ')}. Flats are normalised to sharps.`,
        },
        mode: { type: 'string', enum: MODES, description: 'Scale mode: major or minor.' },
        swing: swingProp(`Swing amount, ${SWING_MIN} to ${SWING_MAX} (0 straight, 30 is a classic MPC feel, 60 heavy).`),
        style: {
          type: 'string',
          enum: STYLES,
          description: 'Sound preset: lofi, house or synthwave. Changes drum tables, filter, reverb and defaults.',
        },
        metronome: { type: 'boolean', description: 'true turns the metronome click on, false turns it off.' },
        transpose_content: {
          type: 'boolean',
          default: true,
          description: 'When key or mode changes: true (default) transposes existing notes, false leaves them as they are.',
        },
      },
      required: [],
      additionalProperties: false,
    },
  },

  add_chords: {
    name: 'add_chords',
    group: 'writers',
    description:
      'Write a 4-bar chord progression to the chords track from Roman numerals or chord names (1 or 2 repeat ' +
      'to fill 4 bars), voiced smoothly. ' +
      'Use when the human wants chords, harmony or a progression under a melody; for single notes use write_notes. ' +
      'Replaces the chords track. ' +
      WRITER_SENTENCE,
    inputSchema: {
      type: 'object',
      properties: {
        progression: progressionProp(
          'One chord per bar, 1 to 4 entries: numerals (i, VI, III, VII, bVII, V7) or names (Am, F, Bb, G7).',
        ),
        rhythm: rhythmProp(
          'Articulation: pad (held), stab (short hits), arp (arpeggio), pulse (8ths), offbeat. Default per style.',
        ),
      },
      required: ['progression'],
      additionalProperties: false,
    },
  },

  add_bass: {
    name: 'add_bass',
    group: 'writers',
    description:
      'Generate a bass line on the bass track that follows the current chord progression using a named pattern. ' +
      'Use when the human asks for bass, a bassline or low end under the chords. ' +
      'Requires chords first (call add_chords); otherwise returns error needs_chords. ' +
      WRITER_SENTENCE,
    inputSchema: {
      type: 'object',
      properties: {
        pattern: bassPatternProp(
          'Bass pattern: root, root_fifth, octave, walking, or follow_kick (root on every kick). Default root.',
        ),
        octave: {
          type: 'integer',
          enum: [1, 2],
          default: 2,
          description: 'Register: 2 (default, D2 to D3) or 1 (an octave lower, sub bass).',
        },
      },
      required: [],
      additionalProperties: false,
    },
  },

  add_drums: {
    name: 'add_drums',
    group: 'writers',
    description:
      'Write a whole drum beat (kick, snare or clap, hats, percussion) for all 4 bars from the style tables ' +
      'at a chosen density, with an optional bar-4 fill. Use when the human asks for drums, a beat or a ' +
      'groove; to change one lane use set_drum_steps. Replaces the drum track. ' +
      WRITER_SENTENCE,
    inputSchema: {
      type: 'object',
      properties: {
        density: densityProp('Busyness: sparse (kick and snare only), medium (default) or busy (16th hats, ghosts).'),
        fill: { type: 'boolean', default: true, description: 'true (default) adds a snare fill in bar 4; false keeps bar 4 plain.' },
        style: {
          type: 'string',
          enum: STYLES,
          description: 'Pattern table to use: lofi, house or synthwave. Defaults to the session style.',
        },
      },
      required: [],
      additionalProperties: false,
    },
  },

  write_notes: {
    name: 'write_notes',
    group: 'writers',
    description:
      'Write single notes (note names or scale degrees at bar/step) to bass, chords or lead, merging with ' +
      'or replacing what is there. Use when the human wants a melody, riff, hook or note edit; for a chord ' +
      'progression use add_chords. Max 64 notes; pitches clamp to the track range. ' +
      WRITER_SENTENCE,
    inputSchema: {
      type: 'object',
      properties: {
        track: {
          type: 'string',
          enum: MELODIC_TRACK_IDS,
          description: `Melodic track to write: ${MELODIC_LIST}. Drums use set_drum_steps.`,
        },
        notes: {
          type: 'array',
          minItems: 1,
          maxItems: 64,
          description: 'Notes to write, 1 to 64. Each has bar, step, note and optional octave, length, velocity.',
          items: {
            type: 'object',
            description: 'One note placed at a bar and 16th step.',
            properties: {
              bar: { type: 'integer', minimum: 1, maximum: 4, description: 'Bar, 1 to 4.' },
              step: { type: 'integer', minimum: 1, maximum: 16, description: '16th step within the bar, 1 to 16 (1 is the downbeat).' },
              note: {
                type: 'string',
                minLength: 1,
                maxLength: 4,
                // Upper-case letters only, so "b3" is always the flat third and never B3.
                pattern: '^([A-G][#b]?[0-8]|[#b]?[1-7])$',
                description: 'Note name with octave, upper-case (C4, F#3, Bb2), or scale degree 1 to 7 with b or # prefix (b3, #4).',
              },
              octave: {
                type: 'integer',
                minimum: 1,
                maximum: 7,
                description: 'Octave for a scale degree, 1 to 7. Default: the track home octave (bass 2, chords 4, lead 4).',
              },
              length: { type: 'integer', minimum: 1, maximum: 16, default: 1, description: 'Length in 16th steps, 1 to 16. Default 1.' },
              velocity: {
                type: 'number',
                minimum: 0.1,
                maximum: 1,
                default: 0.8,
                description: 'Loudness 0.1 to 1. Default 0.8; 0.9 and above renders as an accent.',
              },
            },
            required: ['bar', 'step', 'note'],
            additionalProperties: false,
          },
        },
        mode: {
          type: 'string',
          enum: ['merge', 'replace'],
          default: 'merge',
          description: 'merge (default) adds to existing notes; replace clears the track first (asks if human notes go).',
        },
      },
      required: ['track', 'notes'],
      additionalProperties: false,
    },
  },

  set_drum_steps: {
    name: 'set_drum_steps',
    group: 'writers',
    description:
      'Set one drum lane to a 16-step pattern of X (accent), x (hit), o (ghost) and . (rest), for all or ' +
      'given bars. Use when the human wants to tweak one lane (more hats, kick on every beat, ghost ' +
      'snares); for a whole beat use add_drums. The pattern is exactly 16 characters. ' +
      WRITER_SENTENCE,
    inputSchema: {
      type: 'object',
      properties: {
        lane: { type: 'string', enum: DRUM_LANES, description: `Drum lane: ${DRUM_LANES.join(', ')}.` },
        pattern: {
          type: 'string',
          minLength: 16,
          maxLength: 16,
          pattern: '^[Xxo.\\-01]{16}$',
          description: '16 characters, one per 16th: X accent, x hit, o ghost, . rest (- and 0 rest, 1 hit). E.g. x.o.x.o.x.o.x.o.',
        },
        bars: barsProp('Bars to write, 1 to 4 (1 to 4 entries). Omit to write all four bars.'),
      },
      required: ['lane', 'pattern'],
      additionalProperties: false,
    },
  },

  set_track: {
    name: 'set_track',
    group: 'writers',
    description:
      'Mute, unmute, solo or set the volume of one track without touching its notes; idempotent. ' +
      'Use when the human asks to silence, bring back, isolate, turn up or turn down a track. ' +
      'Only the fields you pass change. ' +
      WRITER_SENTENCE,
    inputSchema: {
      type: 'object',
      properties: {
        track: trackProp(`Track to change: ${TRACK_LIST}.`),
        muted: { type: 'boolean', description: 'true mutes the track, false unmutes it.' },
        solo: { type: 'boolean', description: 'true solos the track (others go quiet), false clears solo.' },
        volume_db: {
          type: 'number',
          minimum: VOLUME_MIN_DB,
          maximum: VOLUME_MAX_DB,
          description: `Track gain in dB, ${VOLUME_MIN_DB} to ${VOLUME_MAX_DB}. Defaults: drums -4, bass -8, chords -12, lead -10.`,
        },
      },
      required: ['track'],
      additionalProperties: false,
    },
  },

  clear_track: {
    name: 'clear_track',
    group: 'writers',
    annotations: { consequentialHint: true },
    description:
      'Remove every note or hit from one track, human and agent alike; the human confirms first when the ' +
      'track is not empty. Use when the human explicitly says to wipe, clear, delete or empty a track. ' +
      'Never call it to make room before writing; other writers replace on their own. ' +
      WRITER_SENTENCE,
    inputSchema: {
      type: 'object',
      properties: {
        track: trackProp(`Track to empty: ${TRACK_LIST}.`),
      },
      required: ['track'],
      additionalProperties: false,
    },
  },

  // ---- dialog ----
  audition: {
    name: 'audition',
    group: 'dialog',
    description:
      'Play the human 2 or 3 variants (progression, rhythm, bass pattern, drum density, tempo, swing) in ' +
      'the tab and wait for them to keep one; the pick is applied as one undoable action. Use when the ' +
      'human asks to hear or compare options, or you cannot pick between two ideas by theory. Waits up to ' +
      'wait_seconds, then returns awaiting_human, card open.',
    inputSchema: {
      type: 'object',
      properties: {
        question: {
          type: 'string',
          maxLength: 120,
          description: 'Question shown above the options, up to 120 characters, e.g. "Walking or octave bass?".',
        },
        options: {
          type: 'array',
          minItems: 2,
          maxItems: 3,
          description: '2 or 3 variants to compare. Each has a label, an optional why, and the changes it applies.',
          items: {
            type: 'object',
            description: 'One variant the human can preview and keep.',
            properties: {
              label: { type: 'string', minLength: 1, maxLength: 40, description: 'Short name on the button, 1 to 40 characters, e.g. "Walking".' },
              why: { type: 'string', maxLength: 80, description: 'One-line reason to consider it, up to 80 characters.' },
              changes: {
                type: 'object',
                description: 'What this variant changes versus the current session. Omitted fields stay as they are.',
                properties: {
                  progression: progressionProp('Chord progression, 1 to 4 chords (numerals or names), written to the chords track.'),
                  rhythm: rhythmProp('Chord rhythm: pad, stab, arp, pulse or offbeat.'),
                  bass_pattern: bassPatternProp('Bass pattern: root, root_fifth, octave, walking or follow_kick.'),
                  drum_density: densityProp('Drum density: sparse, medium or busy.'),
                  tempo: tempoProp(`Tempo in BPM, ${TEMPO_MIN} to ${TEMPO_MAX}.`),
                  swing: swingProp(`Swing amount, ${SWING_MIN} to ${SWING_MAX}.`),
                },
                required: [],
                additionalProperties: false,
              },
            },
            required: ['label', 'changes'],
            additionalProperties: false,
          },
        },
        wait_seconds: {
          type: 'integer',
          minimum: 5,
          maximum: 60,
          default: 25,
          description: 'How long to wait for the human to choose, 5 to 60 seconds. Default 25.',
        },
      },
      required: ['options'],
      additionalProperties: false,
    },
  },

  undo: {
    name: 'undo',
    group: 'dialog',
    description:
      'Revert the most recent agent actions, one or more steps, or everything back to a given undo_id. ' +
      'Use when the human says undo, revert, go back, or dislikes what you just added. ' +
      "Refuses with reason top_is_human_action when the newest change is the human's own work (they undo with Cmd+Z).",
    inputSchema: {
      type: 'object',
      properties: {
        undo_id: { type: 'string', minLength: 1, maxLength: 40, description: 'Revert to just before this undo_id (as returned by a writer). Overrides steps.' },
        steps: { type: 'integer', minimum: 1, maximum: 5, default: 1, description: 'How many agent actions to undo, 1 to 5. Default 1.' },
      },
      required: [],
      additionalProperties: false,
    },
  },

  // ---- transport ----
  transport: {
    name: 'transport',
    group: 'transport',
    description:
      'Start or stop loop playback, optionally from a given bar, and report the playhead position and audio state. ' +
      'Use when the human asks to play, start, stop, pause, or hear it from bar 3. ' +
      'Before the first tap in the tab, play returns audio muted_until_user_taps; ask the human to press Play once.',
    inputSchema: {
      type: 'object',
      properties: {
        action: { type: 'string', enum: ['play', 'stop'], description: 'play starts the loop (or keeps it running); stop halts it.' },
        from_bar: { type: 'integer', minimum: 1, maximum: 4, description: 'Bar to start from when playing, 1 to 4. Default: bar 1.' },
      },
      required: ['action'],
      additionalProperties: false,
    },
  },

  export_midi: {
    name: 'export_midi',
    group: 'transport',
    description:
      'Build a Standard MIDI File of all four tracks and arm the Download button in the header with it; nothing ' +
      'downloads until the human clicks. ' +
      'Use when the human wants to export, save, or take the loop into a DAW such as Logic, Ableton or GarageBand. ' +
      'Filename is letters, digits, _ or - only, 1 to 40 characters; the .mid extension is added.',
    inputSchema: {
      type: 'object',
      properties: {
        filename: {
          type: 'string',
          minLength: 1,
          maxLength: 40,
          pattern: '^[A-Za-z0-9_-]{1,40}$',
          description: 'File name without extension: letters, digits, _ or -, 1 to 40 characters. Default bandmate.',
        },
      },
      required: [],
      additionalProperties: false,
    },
  },

  // ---- recording ----
  stop_recording: {
    name: 'stop_recording',
    group: 'recording',
    description:
      'Disarm live recording on the lead track and report how many notes were captured and which bars they touched; ' +
      'the writer tools come back afterwards. ' +
      'Use when the human says stop recording, done, or that is the take. ' +
      'Only registered while recording is armed.',
    inputSchema: EMPTY,
  },
};

// ---------------------------------------------------------------------------
// Input types (one per tool, matching the schemas exactly)
// ---------------------------------------------------------------------------

/** Accepted key spelling (sharps and flats), see `KEY_SPELLINGS` in types.ts. */
export type KeySpelling = 'C' | 'C#' | 'Db' | 'D' | 'D#' | 'Eb' | 'E' | 'F' | 'F#' | 'Gb' | 'G' | 'G#' | 'Ab' | 'A' | 'A#' | 'Bb' | 'B';

/** A bar number as the tools see it (1-based). */
export type BarNumber = 1 | 2 | 3 | 4;

/** `get_session` takes no input. */
export type GetSessionInput = Record<string, never>;

/** `get_track` input. */
export interface GetTrackInput {
  track: TrackId;
  /** 1 to 4 distinct bars; omitted = all. */
  bars?: BarNumber[];
}

/** `suggest_chords` input. */
export interface SuggestChordsInput {
  /** 1..5, default 3. */
  n?: number;
}

/** `set_groove` input; every field optional, only passed fields change. */
export interface SetGrooveInput {
  /** 60..180 */
  tempo?: number;
  key?: KeySpelling;
  mode?: Mode;
  /** 0..60 */
  swing?: number;
  style?: StyleId;
  metronome?: boolean;
  /** default true */
  transpose_content?: boolean;
}

/** `add_chords` input. */
export interface AddChordsInput {
  /** 1..4 numerals or chord names. */
  progression: string[];
  rhythm?: ChordRhythm;
}

/** `add_bass` input. */
export interface AddBassInput {
  /** default 'root' */
  pattern?: BassPattern;
  /** default 2 */
  octave?: 1 | 2;
}

/** `add_drums` input. */
export interface AddDrumsInput {
  /** default 'medium' */
  density?: Density;
  /** default true */
  fill?: boolean;
  style?: StyleId;
}

/** One note as accepted by `write_notes`. */
export interface NoteSpec {
  bar: BarNumber;
  /** 1..16 */
  step: number;
  /** Upper-case note name with octave (`C4`, `Bb2`) or scale degree (`1`..`7`, `b3`, `#4`). */
  note: string;
  /** 1..7; default the track's home octave. */
  octave?: number;
  /** 1..16, default 1. */
  length?: number;
  /** 0.1..1, default 0.8. */
  velocity?: number;
}

/** `write_notes` input. */
export interface WriteNotesInput {
  track: MelodicTrackId;
  /** 1..64 notes. */
  notes: NoteSpec[];
  /** default 'merge' */
  mode?: 'merge' | 'replace';
}

/** `set_drum_steps` input. */
export interface SetDrumStepsInput {
  lane: DrumLane;
  /** Exactly 16 characters of `X x o . - 0 1`. */
  pattern: string;
  /** 1 to 4 distinct bars; omitted = all. */
  bars?: BarNumber[];
}

/** `set_track` input. */
export interface SetTrackInput {
  track: TrackId;
  muted?: boolean;
  solo?: boolean;
  /** -24..6 */
  volume_db?: number;
}

/** `clear_track` input. */
export interface ClearTrackInput {
  track: TrackId;
}

/** The changes one audition option applies on top of the current session. */
export interface AuditionChanges {
  progression?: string[];
  rhythm?: ChordRhythm;
  bass_pattern?: BassPattern;
  drum_density?: Density;
  /** 60..180 */
  tempo?: number;
  /** 0..60 */
  swing?: number;
}

/** One audition option. */
export interface AuditionOption {
  /** 1..40 chars */
  label: string;
  /** ≤ 80 chars */
  why?: string;
  changes: AuditionChanges;
}

/** `audition` input. */
export interface AuditionInput {
  /** ≤ 120 chars */
  question?: string;
  /** 2 or 3 options. */
  options: AuditionOption[];
  /** 5..60, default 25. */
  wait_seconds?: number;
}

/** `undo` input. */
export interface UndoInput {
  undo_id?: string;
  /** 1..5, default 1. */
  steps?: number;
}

/** `transport` input. */
export interface TransportInput {
  action: 'play' | 'stop';
  from_bar?: BarNumber;
}

/** `export_midi` input. */
export interface ExportMidiInput {
  /** `[A-Za-z0-9_-]{1,40}`, default "bandmate". */
  filename?: string;
}

/** `stop_recording` takes no input. */
export type StopRecordingInput = Record<string, never>;

/** Map from tool name to its input type. */
export interface ToolInputs {
  get_session: GetSessionInput;
  get_track: GetTrackInput;
  suggest_chords: SuggestChordsInput;
  set_groove: SetGrooveInput;
  add_chords: AddChordsInput;
  add_bass: AddBassInput;
  add_drums: AddDrumsInput;
  write_notes: WriteNotesInput;
  set_drum_steps: SetDrumStepsInput;
  set_track: SetTrackInput;
  clear_track: ClearTrackInput;
  audition: AuditionInput;
  undo: UndoInput;
  transport: TransportInput;
  export_midi: ExportMidiInput;
  stop_recording: StopRecordingInput;
}

/** Group of a tool, by name. */
export function groupOf(name: ToolName): ToolGroup {
  return TOOLS[name].group;
}

/** Tool names registered in a given app mode (SPEC §5: idle 15, recording 8, auditioning 7). */
export function toolsForMode(mode: 'idle' | 'recording' | 'auditioning'): ToolName[] {
  const always = [...TOOL_GROUPS.readers, ...TOOL_GROUPS.dialog, ...TOOL_GROUPS.transport];
  if (mode === 'idle') return [...TOOL_GROUPS.readers, ...TOOL_GROUPS.writers, ...TOOL_GROUPS.dialog, ...TOOL_GROUPS.transport];
  if (mode === 'recording') return [...always, ...TOOL_GROUPS.recording];
  return always;
}
